import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import {
  useBlocker,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

import { fmtDMYFromYMD, toISODate } from "../../../../../shared/utils/dateFormat";
import {
  approvePurchase,
  getPurchase,
  getPurchaseCategoryOptions,
  getSupplierPurchaseItems,
  updatePurchase,
} from "../api/purchasesApi";
import PurchaseCardShell from "../components/PurchaseCardShell";
import PurchaseActionDialog from "../components/PurchaseActionDialog";
import PurchaseDetailHeader from "../components/PurchaseDetailHeader";
import PurchaseItemsToolbar from "../components/PurchaseItemsToolbar";
import PurchaseLinesTable from "../components/PurchaseLinesTable";
import UnsavedPurchaseDialog from "../components/UnsavedPurchaseDialog";
import { getPurchaseErrorMessage } from "../utils/purchaseError";
import {
  buildUpdatePurchasePayload,
  buildVisiblePurchaseItemRows,
  normalizePurchase,
  normalizeSupplierPurchaseItem,
  parseNonNegativeInteger,
  purchaseDraftSignature,
  purchaseLineToDraft,
  PURCHASE_STATUS,
  validatePurchaseLines,
} from "../utils/purchaseModel";

const SEARCH_DELAY_MS = 300;

function unwrapHttp(response) {
  if (
    response &&
    typeof response === "object" &&
    "data" in response &&
    !Array.isArray(response.data)
  ) {
    return response.data;
  }
  return response;
}

function normalizeCategories(response) {
  const payload = unwrapHttp(response);
  return (Array.isArray(payload) ? payload : [])
    .filter((category) => category?.id && category?.name)
    .map((category) => ({ id: Number(category.id), name: String(category.name) }))
    .sort((left, right) =>
      left.name.localeCompare(right.name, "es", { sensitivity: "base" })
    );
}

export default function PurchaseDetailPage() {
  const { purchaseId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const baselineSignatureRef = useRef("");
  const latestItemsRequestId = useRef(0);
  const savingRef = useRef(false);
  const [purchase, setPurchase] = useState(null);
  const [lines, setLines] = useState([]);
  const [availableItems, setAvailableItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [viewMode, setViewMode] = useState("selected");
  const [loading, setLoading] = useState(true);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingAndApproving, setSavingAndApproving] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [itemsError, setItemsError] = useState("");
  const [categoriesError, setCategoriesError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [reloadVersion, setReloadVersion] = useState(0);
  const [itemsReloadVersion, setItemsReloadVersion] = useState(0);
  const [categoriesReloadVersion, setCategoriesReloadVersion] = useState(0);
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [approvalError, setApprovalError] = useState("");

  const currentSignature = useMemo(() => purchaseDraftSignature(lines), [lines]);
  const dirty = Boolean(purchase) && currentSignature !== baselineSignatureRef.current;
  const readOnly = purchase?.status === PURCHASE_STATUS.APPROVED;
  const allReceiptsComplete =
    !readOnly &&
    lines.length > 0 &&
    lines.every((line) => {
      const quantity = parseNonNegativeInteger(line.receivedQuantity, {
        optional: true,
      });
      return quantity !== null && quantity !== undefined;
    });

  const navigationBlocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirty &&
      `${currentLocation.pathname}${currentLocation.search}` !==
        `${nextLocation.pathname}${nextLocation.search}`
  );

  useEffect(() => {
    if (navigationBlocker.state === "blocked") setDiscardDialogOpen(true);
  }, [navigationBlocker.state]);

  useEffect(() => {
    if (!dirty) return undefined;
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [dirty]);

  useEffect(() => {
    const timeout = setTimeout(() => setSearch(searchInput.trim()), SEARCH_DELAY_MS);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  useEffect(() => {
    const controller = new AbortController();
    async function fetchCategories() {
      setCategoriesLoading(true);
      setCategoriesError("");
      try {
        const response = await getPurchaseCategoryOptions({ signal: controller.signal });
        if (!controller.signal.aborted) setCategories(normalizeCategories(response));
      } catch (error) {
        if (controller.signal.aborted) return;
        setCategories([]);
        setCategoriesError(
          getPurchaseErrorMessage(error, "No se pudieron cargar las categorías.")
        );
      } finally {
        if (!controller.signal.aborted) setCategoriesLoading(false);
      }
    }
    fetchCategories();
    return () => controller.abort();
  }, [categoriesReloadVersion]);

  useEffect(() => {
    const controller = new AbortController();
    async function fetchPurchaseDetail() {
      setLoading(true);
      setLoadError("");
      try {
        const response = await getPurchase(purchaseId, { signal: controller.signal });
        if (controller.signal.aborted) return;
        const normalized = normalizePurchase(unwrapHttp(response));
        const draftLines = normalized.lines.map(purchaseLineToDraft);
        baselineSignatureRef.current = purchaseDraftSignature(draftLines);
        setPurchase(normalized);
        setLines(draftLines);
        setValidationErrors({});
        setSaveError("");
      } catch (error) {
        if (controller.signal.aborted) return;
        setPurchase(null);
        setLines([]);
        setLoadError(
          getPurchaseErrorMessage(error, "No se pudo cargar la Lista de compra.")
        );
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }
    fetchPurchaseDetail();
    return () => controller.abort();
  }, [purchaseId, reloadVersion]);

  useEffect(() => {
    if (!purchase?.supplier?.id) return undefined;
    const controller = new AbortController();
    const requestId = ++latestItemsRequestId.current;
    async function fetchAvailableItems() {
      setItemsLoading(true);
      setItemsError("");
      try {
        const response = await getSupplierPurchaseItems(purchase.supplier.id, {
          search: search || undefined,
          categoryId: categoryId || undefined,
          signal: controller.signal,
        });
        if (controller.signal.aborted || requestId !== latestItemsRequestId.current) return;
        const payload = unwrapHttp(response);
        setAvailableItems(
          (Array.isArray(payload) ? payload : []).map(normalizeSupplierPurchaseItem)
        );
      } catch (error) {
        if (controller.signal.aborted || requestId !== latestItemsRequestId.current) return;
        setAvailableItems([]);
        setItemsError(
          getPurchaseErrorMessage(
            error,
            "No se pudieron cargar los ítems disponibles del proveedor."
          )
        );
      } finally {
        if (!controller.signal.aborted && requestId === latestItemsRequestId.current) {
          setItemsLoading(false);
        }
      }
    }
    fetchAvailableItems();
    return () => controller.abort();
  }, [categoryId, itemsReloadVersion, purchase?.supplier?.id, search]);

  const selectedById = useMemo(
    () => new Map(lines.map((line) => [Number(line.purchaseItemId), line])),
    [lines]
  );

  const visibleRows = useMemo(() => {
    return buildVisiblePurchaseItemRows({
      lines,
      availableItems,
      viewMode,
      filtersActive: Boolean(search || categoryId),
    });
  }, [availableItems, categoryId, lines, search, viewMode]);

  const creationDate = purchase?.createdAt
    ? fmtDMYFromYMD(toISODate(purchase.createdAt))
    : "—";

  const goBack = useCallback(() => {
    navigate("/stock/operacion/compras", { state: location.state });
  }, [location.state, navigate]);

  function handleFieldChange(purchaseItemId, field, value) {
    if (readOnly || saving) return;
    setLines((current) =>
      current.map((line) =>
        Number(line.purchaseItemId) === Number(purchaseItemId)
          ? { ...line, [field]: value }
          : line
      )
    );
    setValidationErrors((current) => {
      if (!current[purchaseItemId]?.[field]) return current;
      const nextLineErrors = { ...current[purchaseItemId] };
      delete nextLineErrors[field];
      const next = { ...current };
      if (Object.keys(nextLineErrors).length) next[purchaseItemId] = nextLineErrors;
      else delete next[purchaseItemId];
      return next;
    });
    setSaveError("");
  }

  function handleItemSearchChange(value) {
    setSearchInput(value);
    if (value.trim()) setViewMode("all");
  }

  function handleAdd(line) {
    if (readOnly || saving || selectedById.has(Number(line.purchaseItemId))) return;
    setLines((current) => [...current, { ...line, isSelected: undefined }]);
    setSaveError("");
  }

  function handleRemove(purchaseItemId) {
    if (readOnly || saving) return;
    setLines((current) =>
      current.filter((line) => Number(line.purchaseItemId) !== Number(purchaseItemId))
    );
    setValidationErrors((current) => {
      const next = { ...current };
      delete next[purchaseItemId];
      return next;
    });
    setSaveError("");
  }

  function applyPurchaseResponse(response) {
    const normalized = normalizePurchase(unwrapHttp(response));
    const savedLines = normalized.lines.map(purchaseLineToDraft);
    baselineSignatureRef.current = purchaseDraftSignature(savedLines);
    setPurchase(normalized);
    setLines(savedLines);
    setValidationErrors({});
    return normalized;
  }

  async function handleSave() {
    if (!dirty || readOnly || savingRef.current) return;
    const errors = validatePurchaseLines(lines);
    setValidationErrors(errors);
    if (Object.keys(errors).length) return;

    savingRef.current = true;
    setSaving(true);
    setSaveError("");
    try {
      const response = await updatePurchase(
        purchase.id,
        buildUpdatePurchasePayload(lines)
      );
      applyPurchaseResponse(response);
    } catch (error) {
      setSaveError(
        getPurchaseErrorMessage(
          error,
          "No se pudo guardar la Lista de compra. Corregí los datos e intentá nuevamente."
        )
      );
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }

  function requestSaveAndApprove() {
    if (!allReceiptsComplete || readOnly || savingRef.current) return;
    const errors = validatePurchaseLines(lines);
    setValidationErrors(errors);
    if (Object.keys(errors).length) return;

    setSaveError("");
    setApprovalError("");
    setApprovalDialogOpen(true);
  }

  async function confirmSaveAndApprove() {
    if (!allReceiptsComplete || readOnly || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setSavingAndApproving(true);
    setApprovalError("");

    try {
      if (dirty) {
        const savedResponse = await updatePurchase(
          purchase.id,
          buildUpdatePurchasePayload(lines)
        );
        applyPurchaseResponse(savedResponse);
      }

      const approvedResponse = await approvePurchase(purchase.id);
      applyPurchaseResponse(approvedResponse);
      setApprovalDialogOpen(false);
    } catch (error) {
      setApprovalError(
        getPurchaseErrorMessage(
          error,
          "No se pudo guardar y aprobar la compra. Los datos cargados se conservaron."
        )
      );
    } finally {
      savingRef.current = false;
      setSaving(false);
      setSavingAndApproving(false);
    }
  }

  function closeApprovalDialog() {
    if (savingRef.current) return;
    setApprovalError("");
    setApprovalDialogOpen(false);
  }

  function keepEditing() {
    setDiscardDialogOpen(false);
    if (navigationBlocker.state === "blocked") navigationBlocker.reset();
  }

  function discardChanges() {
    setDiscardDialogOpen(false);
    if (navigationBlocker.state === "blocked") navigationBlocker.proceed();
  }

  if (loading) {
    return (
      <Stack spacing={2} sx={{ p: 2, flex: 1, minHeight: 0 }}>
        <PurchaseDetailHeader purchase={null} creationDate="" onBack={goBack} />
        <PurchaseCardShell>
          <Stack sx={{ height: "100%", alignItems: "center", justifyContent: "center" }}>
            <CircularProgress />
            <Typography sx={{ mt: 1.5, color: "primary.900", fontSize: 13, fontWeight: 700 }}>
              Cargando Lista de compra...
            </Typography>
          </Stack>
        </PurchaseCardShell>
      </Stack>
    );
  }

  if (loadError || !purchase) {
    return (
      <Stack spacing={2} sx={{ p: 2, flex: 1 }}>
        <PurchaseDetailHeader purchase={null} creationDate="" onBack={goBack} />
        <Alert
          severity="error"
          action={
            <Button color="inherit" onClick={() => setReloadVersion((current) => current + 1)}>
              Reintentar
            </Button>
          }
        >
          {loadError || "No se encontró la compra."}
        </Alert>
        <Button onClick={goBack} sx={{ alignSelf: "flex-start" }}>
          Volver a Compras
        </Button>
      </Stack>
    );
  }

  return (
    <Stack
      spacing={2}
      sx={{ p: 2, flex: 1, minHeight: 0, overflow: { xs: "auto", md: "hidden" } }}
    >
      <PurchaseDetailHeader
        purchase={purchase}
        creationDate={creationDate}
        onBack={goBack}
      />

      <PurchaseItemsToolbar
        search={searchInput}
        categoryId={categoryId}
        categories={categories}
        categoriesLoading={categoriesLoading}
        viewMode={viewMode}
        disabled={saving}
        onSearchChange={handleItemSearchChange}
        onCategoryChange={setCategoryId}
        onViewModeChange={setViewMode}
      />

      {readOnly ? (
        <Alert severity="info" sx={{ borderRadius: "16px" }}>
          Esta compra está aprobada y se muestra en modo de solo lectura.
        </Alert>
      ) : null}
          {saveError ? (
            <Alert
              severity="error"
              role="alert"
              onClose={() => setSaveError("")}
              sx={{ borderRadius: "16px" }}
            >
              {saveError}
            </Alert>
          ) : null}
          {itemsError ? (
            <Alert
              severity="error"
              role="alert"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => setItemsReloadVersion((current) => current + 1)}
                >
                  Reintentar
                </Button>
              }
              sx={{ borderRadius: "16px" }}
            >
              {itemsError}
            </Alert>
          ) : null}
          {categoriesError ? (
            <Alert
              severity="error"
              role="alert"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => setCategoriesReloadVersion((current) => current + 1)}
                >
                  Reintentar categorías
                </Button>
              }
              sx={{ borderRadius: "16px" }}
            >
              {categoriesError}
            </Alert>
          ) : null}
          {validationErrors.form ? (
            <Alert severity="warning" sx={{ borderRadius: "16px" }}>
              {validationErrors.form}
            </Alert>
          ) : null}

      <PurchaseCardShell
        sx={{
          bgcolor: "neutral.50",
          borderRadius: "24px",
          p: 0,
          boxShadow: "inset 0px 0px 3px rgba(117,117,138,.28)",
        }}
      >
        <Stack spacing={1.25} sx={{ height: "100%", minHeight: 0 }}>
          <PurchaseLinesTable
            rows={visibleRows}
            loading={itemsLoading}
            readOnly={readOnly || saving}
            errors={validationErrors}
            onFieldChange={handleFieldChange}
            onAdd={handleAdd}
            onRemove={handleRemove}
            emptyMessage={
              viewMode === "selected"
                ? search || categoryId
                  ? "No hay ítems seleccionados que coincidan con los filtros."
                  : "La compra no tiene ítems seleccionados."
                : "No se encontraron ítems del proveedor."
            }
          />

          <Box sx={{ display: "flex", justifyContent: "flex-end", px: 2, pb: 1.5 }}>
            <Stack direction="row" spacing={1.25}>
              <Button
                type="button"
                variant="outlined"
                disabled={saving}
                onClick={goBack}
                sx={{ minWidth: 110, borderRadius: "22px", textTransform: "none", fontWeight: 800 }}
              >
                Cancelar
              </Button>
              {!readOnly ? (
                <Button
                  type="button"
                  variant="contained"
                  disabled={!dirty || saving}
                  onClick={handleSave}
                  startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveOutlinedIcon />}
                  sx={{ minWidth: 125, borderRadius: "22px", textTransform: "none", fontWeight: 800 }}
                >
                  {saving ? "Guardando..." : "Guardar"}
                </Button>
              ) : null}
              {allReceiptsComplete ? (
                <Button
                  type="button"
                  variant="contained"
                  disabled={saving}
                  onClick={requestSaveAndApprove}
                  startIcon={
                    savingAndApproving ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <CheckCircleOutlineIcon />
                    )
                  }
                  sx={{
                    minWidth: 175,
                    borderRadius: "22px",
                    textTransform: "none",
                    fontWeight: 800,
                  }}
                >
                  {savingAndApproving
                    ? "Guardando y aprobando..."
                    : "Guardar y aprobar"}
                </Button>
              ) : null}
            </Stack>
          </Box>
        </Stack>
      </PurchaseCardShell>

      <UnsavedPurchaseDialog
        open={discardDialogOpen}
        disabled={saving}
        onKeepEditing={keepEditing}
        onDiscard={discardChanges}
      />

      <PurchaseActionDialog
        open={approvalDialogOpen}
        action="approve"
        purchase={purchase}
        loading={savingAndApproving}
        error={approvalError}
        approvalActionLabel="Guardar y aprobar"
        approvalLoadingLabel="Guardando y aprobando..."
        onClose={closeApprovalDialog}
        onConfirm={confirmSaveAndApprove}
      />
    </Stack>
  );
}
